import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import React, { useCallback, useImperativeHandle, useState } from 'react';
import { toast } from 'react-toastify';
import StripeCardElement from '../StripeCardElement';
import './PaymentForm.scss';

export type PaymentFormRef = {
  createPaymentMethod: () => Promise<any>;
  confirmCardPayment: (
    clientSecret: string,
    paymentMethodId: string
  ) => Promise<any>;
};
interface PaymentFormProps {
  onCardValid: (valid: boolean, cardName?: string) => void;
  isCardEmpty: (valid: boolean) => void;
  checkBoxesData: { acknowledgement: boolean; authorization: boolean };
  onCheckBoxChange: (name: string, value: boolean) => void;
  isSubmitted: boolean;
  customerEmail?: string;
  disabled?: boolean;
  className?: string;
  ref?: React.RefObject<PaymentFormRef | null>;
  clientSecret?: string;
  showCardForm?: boolean;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  onCardValid,
  isCardEmpty,
  checkBoxesData,
  onCheckBoxChange,
  isSubmitted,
  customerEmail,
  disabled = false,
  className = '',
  ref,
  clientSecret,
  showCardForm,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [cardName, setCardName] = useState<string | null>(null);

  const createPaymentMethod = async () => {
    const response: { error: string; paymentMethod: any } = {
      error: '',
      paymentMethod: null,
    };
    if (!stripe || !elements) {
      response.error = 'Stripe has not loaded yet. Please try again.';
      return response;
    }

    if (!cardComplete) {
      response.error = 'Please complete your card details.';
      return response;
    }

    try {
      // Create payment method for future use
      if (clientSecret) {
        const result = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: elements.getElement(CardNumberElement)!,
            billing_details: {
              email: customerEmail,
              name: cardName,
            },
          },
        });

        if (result.error) {
          response.error = result.error.message || 'Failed to save card';
          toast.error(response.error);
        }

        if (result.setupIntent) {
          response.paymentMethod = result.setupIntent.payment_method;
        }
      } else {
        const { error: paymentMethodError, paymentMethod } =
          await stripe.createPaymentMethod({
            type: 'card',
            card: elements.getElement(CardNumberElement)!,
            billing_details: {
              email: customerEmail,
              name: cardName,
            },
          });

        if (paymentMethodError) {
          response.error = paymentMethodError.message || 'Failed to save card';
        }

        if (paymentMethod) {
          response.paymentMethod = paymentMethod;
        }
      }
      return response;
    } catch (error) {
      response.error = 'An unexpected error occurred. Please try again.';
      return response;
    } finally {
      if (response.error) {
        setCardError(response.error);
      }
    }
  };

  const confirmCardPayment = async (
    clientSecret: string,
    paymentMethodId: string
  ) => {
    if (!stripe) {
      return;
    }
    return await stripe.confirmCardPayment(clientSecret, {
      payment_method: paymentMethodId,
    });
  };

  useImperativeHandle(ref, () => ({
    createPaymentMethod,
    confirmCardPayment,
  }));

  const handleCardChange = useCallback(
    (complete: boolean, cardName?: string, error?: any) => {
      setCardComplete(complete);
      if (onCardValid) {
        onCardValid(complete, cardName);
        setCardName(cardName || '');
      }
      setCardError(error ? error.message : null);
    },
    [onCardValid]
  );

  const checkIsCardEmpty = (isEmpty: boolean) => {
    isCardEmpty(isEmpty);
  };

  if (!showCardForm) return null;

  return (
    <div className={`payment-form ${className}`}>
      <StripeCardElement
        onCardChange={handleCardChange}
        checkIsCardEmpty={checkIsCardEmpty}
        disabled={disabled}
      />

      <div className="card-setup-info">
        <div className="info-display">
          <i className="cs-icon icon-info"></i>
          <span>
            For your security, our restaurant does not store or have access to
            your credit card details. All payments are processed safely and
            securely by our trusted payment provider (Stripe). Your card details
            are used only to create a payment intent and to automatically deduct
            the amount for your order.
          </span>
        </div>
      </div>

      <div className="card-setup-info-checkbox">
        <div className="checkbox-ui">
          <input
            type="checkbox"
            onChange={(e) =>
              onCheckBoxChange('acknowledgement', e.target.checked)
            }
            className={
              isSubmitted && !checkBoxesData.acknowledgement ? 'error' : ''
            }
            checked={checkBoxesData.acknowledgement}
          />
          <span>
            I understand that the restaurant does not store or access my card
            details, and that all sensitive payment information is managed
            securely by the payment provider (Stripe).
          </span>
        </div>
        <div className="checkbox-ui">
          <input
            type="checkbox"
            onChange={(e) =>
              onCheckBoxChange('authorization', e.target.checked)
            }
            className={
              isSubmitted && !checkBoxesData.authorization ? 'error' : ''
            }
            checked={checkBoxesData.authorization}
          />
          <span>
            I agree to securely save my card with the payment provider (Stripe)
            and authorize the restaurant to automatically deduct payments for my
            orders.
          </span>
        </div>
      </div>

      {cardError && (
        <div className="payment-error">
          <i className="cs-icon icon-error"></i>
          {cardError}
        </div>
      )}
    </div>
  );
};

export default PaymentForm;
